import React, { useEffect, useMemo, useRef } from "react";
import { ReactComponent as Chatbot } from "../icons/Chatbot.svg";
import { ReactComponent as Staff } from "../icons/Staff.svg";
import { CommunitySchema, MessageSchema } from "../data/types";
import MessagesSkeleton from "./MessagesSkeleton";
import CommunityAvatar from "./CommunityAvatar";
import BulletPointIcon from "../icons/BulletPointIcon";
import {
  formatMessageTime,
  formatMessageDateSeparator,
  isSameMessageDay,
  getMarkdownLineType,
  parseMarkdownHeading,
  getMarkdownHeadingClassName,
  renderInlineMarkdown,
} from "../utils/helpers";

type ChatMessageListProps = {
  messages?: MessageSchema[];
  isLoading: boolean;
  currentChatCommunity: string;
  selectedCommunity?: CommunitySchema;
  userAvatar?: string;
  userLabel?: string;
  variant?: "mobile" | "desktop";
  isChatbotTyping?: boolean;
  isStaffTyping?: boolean;
  staffAutoReply?: string | null;
  pendingMessage?: string | null;
};

const isCommunityChat = (currentChatCommunity: string) =>
  currentChatCommunity !== "chatbot" && currentChatCommunity !== "staff";

const FormattedChatbotMessage: React.FC<{ content: string }> = ({ content }) => (
  <div className="space-y-2 break-words pr-1">
    {content.split("\n").map((line, index) => {
      const trimmed = line.trim();
      const lineType = getMarkdownLineType(line);

      if (lineType === "bullet") {
        return (
          <div key={index} className="flex items-start gap-2">
            <div className="w-4 h-4 rounded-full bg-[#5DB6CE] flex items-center justify-center mt-0.5 flex-shrink-0">
              <BulletPointIcon />
            </div>
            <span className="text-gray-900">
              {renderInlineMarkdown(trimmed.substring(1).trim(), `line-${index}`)}
            </span>
          </div>
        );
      }

      if (lineType === "numbered") {
        return (
          <div key={index} className="flex items-start gap-2">
            <span className="text-gray-900 font-semibold">
              {renderInlineMarkdown(trimmed, `line-${index}`)}
            </span>
          </div>
        );
      }

      if (lineType === "heading") {
        const heading = parseMarkdownHeading(line);
        if (!heading) return null;

        const HeadingTag = `h${heading.level}` as keyof JSX.IntrinsicElements;

        return (
          <HeadingTag key={index} className={getMarkdownHeadingClassName(heading.level)}>
            {renderInlineMarkdown(heading.text, `line-${index}`)}
          </HeadingTag>
        );
      }

      if (lineType === "paragraph") {
        return (
          <p key={index} className="text-gray-900">
            {renderInlineMarkdown(trimmed, `line-${index}`)}
          </p>
        );
      }

      return null;
    })}
  </div>
);

const UserAvatar: React.FC<{
  avatar?: string;
  label?: string;
  size: "sm" | "md";
}> = ({ avatar, label, size }) => {
  const sizeClass = size === "sm" ? "h-8 w-8" : "h-9 w-9 sm:h-10 sm:w-10";
  const initial = (label || "?").charAt(0).toUpperCase();

  if (avatar) {
    return (
      <img
        src={avatar}
        alt={label || "You"}
        className={`${sizeClass} rounded-full object-cover border border-gray-200 flex-shrink-0`}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full bg-gray-200 border border-gray-200 flex items-center justify-center flex-shrink-0`}
    >
      <span className="text-xs sm:text-sm font-semibold text-gray-600">{initial}</span>
    </div>
  );
};

// const CommunityAvatar: React.FC<{
//   logo?: string;
//   label?: string;
//   size: "sm" | "md";
// }> = ({ logo, label, size }) => {
//   const sizeClass = size === "sm" ? "h-8 w-8" : "h-9 w-9 sm:h-10 sm:w-10";
//   const initial = (label || "?").charAt(0).toUpperCase();

//   if (logo) {
//     return (
//       <img
//         src={logo}
//         alt={label || "Community"}
//         className={`${sizeClass} rounded-full object-scale-down bg-white border border-gray-200 flex-shrink-0`}
//       />
//     );
//   }

//   return (
//     <div
//       className={`${sizeClass} rounded-full bg-gray-200 border border-gray-200 flex items-center justify-center flex-shrink-0`}
//     >
//       <span className="text-xs sm:text-sm font-semibold text-gray-600">{initial}</span>
//     </div>
//   );
// };

const RespondentAvatar: React.FC<{
  currentChatCommunity: string;
  selectedCommunity?: CommunitySchema;
  size: "sm" | "md";
}> = ({ currentChatCommunity, selectedCommunity, size }) => {
  const sizeClass = size === "sm" ? "h-8 w-8" : "h-9 w-9 sm:h-10 sm:w-10";
  const iconClass = size === "sm" ? "h-4 w-4" : "h-5 w-5 sm:h-6 sm:w-6";

  if (currentChatCommunity === "chatbot") {
    return (
      <div
        aria-label="AI Assistant avatar"
        className={`${sizeClass} rounded-full bg-[#51a0b5]/10 border border-gray-200 flex items-center justify-center flex-shrink-0`}
      >
        <Chatbot className={`${iconClass} text-primaryDark-0/90`} />
      </div>
    );
  }

  if (currentChatCommunity === "staff") {
    return (
      <div
        aria-label="Staff avatar"
        className={`${sizeClass} rounded-full bg-primaryDark-0/10 border border-gray-200 flex items-center justify-center flex-shrink-0`}
      >
        <Staff className={`${iconClass} text-primaryDark-0`} />
      </div>
    );
  }

  if (isCommunityChat(currentChatCommunity)) {
    return (
      <CommunityAvatar
        logo={selectedCommunity?.logo}
        label={selectedCommunity?.name}
        size={size}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0`}
    >
      <Staff className={`${iconClass} text-primaryDark-0/70`} />
    </div>
  );
};

const ChatbotTypingIndicator: React.FC<{
  currentChatCommunity: string;
  selectedCommunity?: CommunitySchema;
  size: "sm" | "md";
}> = ({ currentChatCommunity, selectedCommunity, size }) => (
  <div className="flex w-full justify-start" aria-live="polite" aria-label="AI is responding">
    <div className="flex items-end gap-2 max-w-[85%] sm:max-w-[80%]">
      <RespondentAvatar
        currentChatCommunity={currentChatCommunity}
        selectedCommunity={selectedCommunity}
        size={size}
      />
      <div className="my-auto rounded-xl px-4 py-3 bg-white text-gray-900 border border-[#E9E9E9]">
        <div className="flex items-center gap-1.5 h-5">
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              className="h-2 w-2 rounded-full bg-[#5DB6CE] animate-bounce"
              style={{ animationDelay: `${index * 150}ms`, animationDuration: "0.9s" }}
            />
          ))}
        </div>
        <p className="sr-only">AI is typing</p>
      </div>
    </div>
  </div>
);

const StaffTypingIndicator: React.FC<{
  currentChatCommunity: string;
  selectedCommunity?: CommunitySchema;
  size: "sm" | "md";
}> = ({ currentChatCommunity, selectedCommunity, size }) => (
  <div className="flex w-full justify-start" aria-live="polite" aria-label="Staff is responding">
    <div className="flex items-end gap-2 max-w-[85%] sm:max-w-[80%]">
      <RespondentAvatar
        currentChatCommunity={currentChatCommunity}
        selectedCommunity={selectedCommunity}
        size={size}
      />
      <div className="my-auto rounded-xl px-4 py-3 bg-white text-gray-900 border border-[#E9E9E9]">
        <div className="flex items-center gap-1.5 h-5">
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              className="h-2 w-2 rounded-full bg-primaryDark-0 animate-bounce"
              style={{ animationDelay: `${index * 150}ms`, animationDuration: "0.9s" }}
            />
          ))}
        </div>
        <p className="sr-only">Staff is typing</p>
      </div>
    </div>
  </div>
);

const StaffAutoReplyMessage: React.FC<{
  content: string;
  currentChatCommunity: string;
  selectedCommunity?: CommunitySchema;
  avatarSize: "sm" | "md";
}> = ({ content, currentChatCommunity, selectedCommunity, avatarSize }) => (
  <div className="flex w-full justify-start">
    <div className="flex items-end gap-2 max-w-[85%] sm:max-w-[80%]">
      <div className="flex flex-col my-auto">
        <RespondentAvatar
          currentChatCommunity={currentChatCommunity}
          selectedCommunity={selectedCommunity}
          size={avatarSize}
        />
      </div>
      <div className="my-auto rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 bg-white text-gray-900 border border-[#E9E9E9]">
        <p className="text-sm sm:text-[13px] font-open-sans font-normal leading-relaxed whitespace-pre-wrap break-words pr-1">
          {content}
        </p>
      </div>
    </div>
  </div>
);

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  isLoading,
  currentChatCommunity,
  selectedCommunity,
  userAvatar,
  userLabel,
  variant = "mobile",
  isChatbotTyping = false,
  isStaffTyping = false,
  staffAutoReply = null,
  pendingMessage = null,
}) => {
  const isMobile = variant === "mobile";
  const avatarSize = isMobile ? "md" : "sm";
  const bottomRef = useRef<HTMLDivElement>(null);
  const showChatbotTyping = isChatbotTyping && currentChatCommunity === "chatbot";
  const showStaffTyping = isStaffTyping && currentChatCommunity === "staff";
  const showStaffAutoReply = Boolean(staffAutoReply) && currentChatCommunity === "staff";
  const messageTimezone = selectedCommunity?.timezone;
  const trimmedPendingMessage = pendingMessage?.trim() || null;
  const threadMessages = useMemo(() => {
    if (!trimmedPendingMessage) return messages;

    const optimisticMessage: MessageSchema = {
      id: "pending-message",
      community_id: currentChatCommunity,
      content: trimmedPendingMessage,
      username: userLabel || "You",
      type: "received",
      created: new Date().toISOString(),
    };

    return [...(messages || []), optimisticMessage];
  }, [messages, trimmedPendingMessage, currentChatCommunity, userLabel]);

  useEffect(() => {
    if (
      isLoading ||
      (!threadMessages?.length && !showChatbotTyping && !showStaffTyping && !showStaffAutoReply)
    ) {
      return;
    }

    const scrollToBottom = () => {
      bottomRef.current?.scrollIntoView?.({ behavior: "auto", block: "end" });
    };

    scrollToBottom();
    const frame = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(frame);
  }, [
    threadMessages,
    currentChatCommunity,
    isLoading,
    showChatbotTyping,
    showStaffTyping,
    showStaffAutoReply,
  ]);

  if (isLoading) {
    return <MessagesSkeleton variant={variant} />;
  }

  if (!threadMessages?.length) {
    if (!showChatbotTyping && !showStaffTyping && !showStaffAutoReply) {
      return (
        <div className="flex items-center justify-center h-full min-h-[120px] text-gray-400 text-sm font-open-sans px-4">
          <p>No messages yet. Start the conversation!</p>
        </div>
      );
    }

    return (
      <div className={`flex flex-col ${isMobile ? "gap-3 sm:gap-4" : "gap-3"} w-full`}>
        {showStaffTyping && (
          <StaffTypingIndicator
            currentChatCommunity={currentChatCommunity}
            selectedCommunity={selectedCommunity}
            size={avatarSize}
          />
        )}
        {showStaffAutoReply && staffAutoReply && (
          <StaffAutoReplyMessage
            content={staffAutoReply}
            currentChatCommunity={currentChatCommunity}
            selectedCommunity={selectedCommunity}
            avatarSize={avatarSize}
          />
        )}
        {showChatbotTyping && (
          <ChatbotTypingIndicator
            currentChatCommunity={currentChatCommunity}
            selectedCommunity={selectedCommunity}
            size={avatarSize}
          />
        )}
        <div ref={bottomRef} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${isMobile ? "gap-3 sm:gap-4" : "gap-3"} w-full`}>
      {threadMessages.map((message, index) => {
        const isReceived = message.type === "sent";
        const showDateSeparator =
          index === 0 ||
          !isSameMessageDay(message.created, threadMessages[index - 1]?.created, messageTimezone);

        return (
          <React.Fragment key={message.id || `${message.created}-${index}`}>
            {showDateSeparator && (
              <p className="text-center text-[10px] sm:text-[10px] text-gray-400 font-open-sans py-1">
                {formatMessageDateSeparator(message.created, undefined, messageTimezone)}
              </p>
            )}
            <div
              className={`flex w-full ${isReceived ? "justify-start" : "justify-end"}`}
            >
              <div
                className={`flex items-end gap-2 max-w-[85%] sm:max-w-[80%] ${
                  isReceived ? "flex-row" : "flex-row-reverse"
                }`}
              >
                <div className="flex flex-col my-auto">
                  {isReceived ? (
                    <RespondentAvatar
                      currentChatCommunity={currentChatCommunity}
                      selectedCommunity={selectedCommunity}
                      size={avatarSize}
                    />
                  ) : (
                    <UserAvatar avatar={userAvatar} label={userLabel} size={avatarSize} />
                  )}
                </div>
                <div
                  className={`my-auto rounded-xl px-3 sm:px-4 py-2.5 sm:py-3  ${
                    isReceived
                      ? "bg-white text-gray-900 border border-[#E9E9E9]"
                      : "bg-[#E9E9E9] text-gray-900"
                  }`}
                >
                  <div className="text-sm sm:text-[13px] font-open-sans font-normal leading-relaxed">
                    {currentChatCommunity === "chatbot" && isReceived ? (
                      <FormattedChatbotMessage content={message.content} />
                    ) : (
                      <p className="whitespace-pre-wrap break-words pr-1">{message.content}</p>
                    )}
                  </div>
                  <p
                    className={`text-[10px] sm:text-[10px] text-gray-400 mt-1 text-right ${
                      isReceived ? "" : "text-gray-500"
                    }`}
                  >
                    {formatMessageTime(message.created, messageTimezone)}
                  </p>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      {showChatbotTyping && (
        <ChatbotTypingIndicator
          currentChatCommunity={currentChatCommunity}
          selectedCommunity={selectedCommunity}
          size={avatarSize}
        />
      )}
      {showStaffTyping && (
        <StaffTypingIndicator
          currentChatCommunity={currentChatCommunity}
          selectedCommunity={selectedCommunity}
          size={avatarSize}
        />
      )}
      {showStaffAutoReply && staffAutoReply && (
        <StaffAutoReplyMessage
          content={staffAutoReply}
          currentChatCommunity={currentChatCommunity}
          selectedCommunity={selectedCommunity}
          avatarSize={avatarSize}
        />
      )}
      <div ref={bottomRef} aria-hidden="true" />
    </div>
  );
};

export default ChatMessageList;
